/**
 * App Component
 *
 * Main application component with routing
 */

import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'

import { AuthProvider } from '@/application/auth'
import { Toaster } from '@/lib/toast'
import { ProtectedRoute, PublicRoute } from '@/presentation/components/auth/ProtectedRoute'
import { PatientProtectedRoute, PatientPublicRoute } from '@/presentation/components/auth/PatientProtectedRoute'
import { DashboardLayout } from '@/presentation/layouts/DashboardLayout'
import { PatientPortalLayout } from '@/presentation/layouts/PatientPortalLayout'
import { LoginPage } from '@/presentation/pages/auth/LoginPage'
import { ForceChangePasswordPage } from '@/presentation/pages/auth/ForceChangePasswordPage'
import { DashboardPage } from '@/presentation/pages/dashboard/DashboardPage'
import { PatientsPage, PatientDetailPage, PatientFormPage } from '@/presentation/pages/patients'
import { StudiesPage, StudyDetailPage } from '@/presentation/pages/studies'
import { StudyAmendPage } from '@/presentation/pages/studies/StudyAmendPage'
import { ReportsPage, ReportDetailPage, ReportFormPage, ReportAmendPage } from '@/presentation/pages/reports'
import { TranscriptionQueuePage, TranscriptionWorkPage } from '@/presentation/pages/transcription'
import { WorklistPage } from '@/presentation/pages/worklist'
import { AuditPage, UsersPage, ConfiguracionPage, SedesPage, ModalitiesPage, TemplatesPage, HotFoldersPage, HotFolderFilesPage, PrioritiesPage, SchedulesPage } from '@/presentation/pages/admin'
import {
  SuperAdminDashboard,
  InstitutionsPage,
  InstitutionDetailPage,
  InstitutionProvisioningPage,
  PlansPage,
  AuditGlobalPage,
  SystemPage,
  CatalogPage,
} from '@/presentation/pages/super-admin'
import { ProfilePage } from '@/presentation/pages/profile'
import { ViewerPage } from '@/presentation/pages/viewer'
import { PatientLoginPage, PatientHomePage, PatientStudyDetailPage, PatientViewerPage } from '@/presentation/pages/patient-portal'
import { AppointmentsPage, AppointmentDetailPage, NewAppointmentPage } from '@/presentation/pages/appointments'
import { MiAgendaPage } from '@/presentation/pages/mi-agenda'
import { APP_ROUTES, USER_ROLES } from '@/config/constants'
import { useAuth } from '@/application/auth'

// =============================================================================
// Role Guard Component
// =============================================================================

function RoleGuard({
  children,
  allowedRoles,
  redirectTo,
}: {
  children: React.ReactNode
  allowedRoles: string[]
  redirectTo?: string
}) {
  const { user } = useAuth()

  if (!user || !allowedRoles.includes(user.role)) {
    // SUPER_ADMIN tiene su propio panel — si llega a una ruta clinica
    // operativa, lo enviamos al dashboard de control plane en vez del
    // dashboard generico.
    const fallback =
      redirectTo ??
      (user?.role === USER_ROLES.SUPER_ADMIN
        ? APP_ROUTES.SUPER_ADMIN.DASHBOARD
        : APP_ROUTES.DASHBOARD)
    return <Navigate to={fallback} replace />
  }

  return <>{children}</>
}

// =============================================================================
// App Component
// =============================================================================

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              className: 'text-sm',
              style: {
                background: 'var(--background)',
                color: 'var(--foreground)',
                border: '1px solid var(--border)',
              },
            }}
          />
          <Routes>
            {/* Public Routes */}
            <Route
              path={APP_ROUTES.LOGIN}
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />

            {/* Patient Portal Login (separate from staff login) */}
            <Route
              path={APP_ROUTES.PATIENT_LOGIN}
              element={
                <PatientPublicRoute>
                  <PatientLoginPage />
                </PatientPublicRoute>
              }
            />

            {/* Forced password change (authenticated users with mustChangePassword) */}
            <Route
              path={APP_ROUTES.CHANGE_PASSWORD}
              element={
                <ProtectedRoute>
                  <ForceChangePasswordPage />
                </ProtectedRoute>
              }
            />

            {/* Full-screen Protected Routes (no dashboard layout) */}
            <Route
              path="/viewer/:id"
              element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.RADIOLOGIST, USER_ROLES.DOCTOR, USER_ROLES.TECHNICIAN]}>
                    <ViewerPage />
                  </RoleGuard>
                </ProtectedRoute>
              }
            />

            {/* Visor del paciente: a pantalla completa, fuera del layout del
                portal, igual que el del personal. Protegido por la misma
                guarda de sesion de paciente. */}
            <Route
              path={APP_ROUTES.PATIENT_PORTAL.VIEWER(':id')}
              element={
                <PatientProtectedRoute>
                  <PatientViewerPage />
                </PatientProtectedRoute>
              }
            />

            {/* Patient Portal Routes */}
            <Route
              element={
                <PatientProtectedRoute>
                  <PatientPortalLayout />
                </PatientProtectedRoute>
              }
            >
              <Route path={APP_ROUTES.PATIENT_PORTAL.HOME} element={<PatientHomePage />} />
              {/* El portal es una sola pantalla: la lista de examenes vive en
                  el inicio. Esta ruta se conserva redirigiendo para que un
                  enlace guardado o compartido no deje al paciente en un 404. */}
              <Route
                path={APP_ROUTES.PATIENT_PORTAL.MY_STUDIES}
                element={<Navigate to={APP_ROUTES.PATIENT_PORTAL.HOME} replace />}
              />
              <Route path="/mi-portal/estudios/:id" element={<PatientStudyDetailPage />} />
            </Route>

            {/* Protected Routes with Dashboard Layout */}
            <Route
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route
                path={APP_ROUTES.DASHBOARD}
                element={
                  <RoleGuard
                    allowedRoles={[
                      USER_ROLES.ADMIN,
                      USER_ROLES.RADIOLOGIST,
                      USER_ROLES.DOCTOR,
                      USER_ROLES.TECHNICIAN,
                      USER_ROLES.TRANSCRIPTOR,
                    ]}
                    redirectTo={APP_ROUTES.SUPER_ADMIN.DASHBOARD}
                  >
                    <DashboardPage />
                  </RoleGuard>
                }
              />
              {/* Profile */}
              <Route path={APP_ROUTES.PROFILE} element={<ProfilePage />} />
              {/* Patient Routes - Protected by role */}
              <Route path={APP_ROUTES.PATIENTS} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.RADIOLOGIST, USER_ROLES.DOCTOR, USER_ROLES.TECHNICIAN]}>
                  <PatientsPage />
                </RoleGuard>
              } />
              <Route path="/patients/new" element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.TECHNICIAN]}>
                  <PatientFormPage />
                </RoleGuard>
              } />
              <Route path="/patients/:id" element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.RADIOLOGIST, USER_ROLES.DOCTOR, USER_ROLES.TECHNICIAN]}>
                  <PatientDetailPage />
                </RoleGuard>
              } />
              <Route path="/patients/:id/edit" element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.TECHNICIAN]}>
                  <PatientFormPage />
                </RoleGuard>
              } />
              {/* Study Routes - Protected by role */}
              <Route path={APP_ROUTES.STUDIES} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.RADIOLOGIST, USER_ROLES.DOCTOR, USER_ROLES.TECHNICIAN]}>
                  <StudiesPage />
                </RoleGuard>
              } />
              <Route path="/studies/:id" element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.RADIOLOGIST, USER_ROLES.DOCTOR, USER_ROLES.TECHNICIAN]}>
                  <StudyDetailPage />
                </RoleGuard>
              } />
              <Route path="/studies/:id/amend" element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.TECHNICIAN]}>
                  <StudyAmendPage />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.WORKLIST} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.RADIOLOGIST, USER_ROLES.DOCTOR, USER_ROLES.TECHNICIAN]}>
                  <WorklistPage />
                </RoleGuard>
              } />
              {/* Report Routes - Only for roles with report access */}
              <Route path={APP_ROUTES.REPORTS} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.RADIOLOGIST, USER_ROLES.DOCTOR]}>
                  <ReportsPage />
                </RoleGuard>
              } />
              <Route path="/reports/new" element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.RADIOLOGIST]}>
                  <ReportFormPage />
                </RoleGuard>
              } />
              <Route path="/reports/:id" element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.RADIOLOGIST, USER_ROLES.DOCTOR]}>
                  <ReportDetailPage />
                </RoleGuard>
              } />
              <Route path="/reports/:id/edit" element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.RADIOLOGIST]}>
                  <ReportFormPage />
                </RoleGuard>
              } />
              <Route path="/reports/:id/amend" element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.RADIOLOGIST]}>
                  <ReportAmendPage />
                </RoleGuard>
              } />

              {/* Transcription Routes (for TRANSCRIPTOR role) */}
              <Route path={APP_ROUTES.TRANSCRIPTION.QUEUE} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.TRANSCRIPTOR]}>
                  <TranscriptionQueuePage />
                </RoleGuard>
              } />
              <Route path="/transcription/:id" element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.TRANSCRIPTOR]}>
                  <TranscriptionWorkPage />
                </RoleGuard>
              } />

              {/* Mi agenda: lo que tiene HOY el profesional que entra.
                  Separada de "Citas" a proposito: esa exige
                  `appointment:view:all` y es de quien agenda; esta exige
                  `view:own` y es de quien atiende. */}
              <Route path={APP_ROUTES.MI_AGENDA} element={
                <RoleGuard allowedRoles={[USER_ROLES.RADIOLOGIST, USER_ROLES.ADMIN, USER_ROLES.TECHNICIAN]}>
                  <MiAgendaPage />
                </RoleGuard>
              } />

              {/* Appointment Routes */}
              <Route path={APP_ROUTES.APPOINTMENTS} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.TECHNICIAN, USER_ROLES.RADIOLOGIST, USER_ROLES.DOCTOR]}>
                  <AppointmentsPage />
                </RoleGuard>
              } />
              <Route path="/appointments/new" element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.TECHNICIAN]}>
                  <NewAppointmentPage />
                </RoleGuard>
              } />
              <Route path="/appointments/:id" element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN, USER_ROLES.TECHNICIAN, USER_ROLES.RADIOLOGIST, USER_ROLES.DOCTOR]}>
                  <AppointmentDetailPage />
                </RoleGuard>
              } />

              {/* Admin Routes */}
              <Route path={APP_ROUTES.ADMIN.USERS} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN]}>
                  <UsersPage />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.ADMIN.AUDIT} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN]}>
                  <AuditPage />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.ADMIN.SETTINGS} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN]}>
                  <ConfiguracionPage />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.ADMIN.SEDES} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN]}>
                  <SedesPage />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.ADMIN.STUDY_TYPES} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN]}>
                  <ModalitiesPage />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.ADMIN.TEMPLATES} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN]}>
                  <TemplatesPage />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.ADMIN.HOT_FOLDERS} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN]}>
                  <HotFoldersPage />
                </RoleGuard>
              } />
              <Route path="/admin/hot-folders/:id/files" element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN]}>
                  <HotFolderFilesPage />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.ADMIN.PRIORITIES} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN]}>
                  <PrioritiesPage />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.ADMIN_SCHEDULES} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN, USER_ROLES.ADMIN]}>
                  <SchedulesPage />
                </RoleGuard>
              } />

              {/* Super Admin Routes */}
              <Route path={APP_ROUTES.SUPER_ADMIN.DASHBOARD} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN]}>
                  <SuperAdminDashboard />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.SUPER_ADMIN.INSTITUTIONS} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN]}>
                  <InstitutionsPage />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.SUPER_ADMIN.INSTITUTION_NEW} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN]}>
                  <InstitutionProvisioningPage />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.SUPER_ADMIN.INSTITUTION_DETAIL(':id')} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN]}>
                  <InstitutionDetailPage />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.SUPER_ADMIN.PLANS} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN]}>
                  <PlansPage />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.SUPER_ADMIN.CATALOG} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN]}>
                  <CatalogPage />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.SUPER_ADMIN.AUDIT} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN]}>
                  <AuditGlobalPage />
                </RoleGuard>
              } />
              <Route path={APP_ROUTES.SUPER_ADMIN.SYSTEM} element={
                <RoleGuard allowedRoles={[USER_ROLES.SUPER_ADMIN]}>
                  <SystemPage />
                </RoleGuard>
              } />
            </Route>

            {/* Redirect root to dashboard or login */}
            <Route path="/" element={<Navigate to={APP_ROUTES.DASHBOARD} replace />} />

            {/* 404 */}
            <Route
              path="*"
              element={
                <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
                  {/*
                    Con salida.

                    Antes esta pantalla era un callejon sin salida: el numero,
                    la frase y nada mas. Sin menu, sin enlace, sin boton. Quien
                    llegaba aqui por un enlace viejo o una direccion mal escrita
                    solo podia salir editando la barra del navegador.
                  */}
                  <div className="text-center">
                    <h1 className="text-6xl font-bold text-[var(--primary)]">404</h1>
                    <p className="text-xl text-[var(--foreground-muted)] mt-4">
                      Página no encontrada
                    </p>
                    <p className="text-[var(--foreground-muted)] mt-2">
                      La dirección que abrió no existe o cambió de sitio.
                    </p>
                    <Link
                      to={APP_ROUTES.DASHBOARD}
                      className="inline-block mt-6 px-5 py-2.5 rounded-md bg-[var(--primary)] text-white font-medium hover:opacity-90"
                    >
                      Volver al inicio
                    </Link>
                  </div>
                </div>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
